// public/v3/src/lib/user-prefs.js — per-browser provider preferences.
//
// Provider-personal toggles persisted to localStorage. No backend storage —
// each operator's browser remembers its own settings. Practice/admin settings
// live in /data/settings (admin-gated) and are surfaced via the v2 advanced
// settings page.

import { DEFAULT_TAB_ORDER, isKnownTab, normalizeHiddenTabs, normalizeToolbarOrder } from './tabs';

const PREFIX = 'ccm.v3.prefs.';

export const PREF_DEFAULTS = Object.freeze({
  density: 'cozy',                 // 'cozy' | 'compact'
  defaultTab: 'timelogs',          // 'timelogs' | 'studio' | 'apcm' | 'onboard'
  defaultReviewTab: 'careplan',    // 'careplan' | 'billing' | 'timelog'
  confirmBeforePush: false,
  keyboardShortcuts: true,
  hideNoChronic: false,           // Hide patients with no chronic conditions
  hiddenOnboardTags: [],          // Lowercase Onboard filter-chip labels to hide
  toolbarOrder: DEFAULT_TAB_ORDER,
  hiddenTabs: [],
  programFilter: 'ccm',           // 'all' | 'ccm' | 'bhi' — chrome segmented filter
});

function normalizeTagPrefs(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  for (const tag of value) {
    const key = String(tag || '').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

function sanitizePref(key, value) {
  switch (key) {
    case 'density':
      return value === 'compact' ? 'compact' : 'cozy';
    case 'defaultTab':
      return isKnownTab(value) ? value : PREF_DEFAULTS.defaultTab;
    case 'defaultReviewTab':
      return ['careplan', 'billing', 'timelog'].includes(value) ? value : PREF_DEFAULTS.defaultReviewTab;
    case 'confirmBeforePush':
    case 'keyboardShortcuts':
    case 'hideNoChronic':
      return !!value;
    case 'hiddenOnboardTags':
      return normalizeTagPrefs(value);
    case 'toolbarOrder':
      return normalizeToolbarOrder(value);
    case 'hiddenTabs':
      return normalizeHiddenTabs(value, getPref('toolbarOrder'));
    case 'programFilter':
      return value === 'all' || value === 'bhi' ? value : 'ccm';
    default:
      return value;
  }
}

export function getPref(key) {
  if (!(key in PREF_DEFAULTS)) return undefined;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw === null) return sanitizePref(key, PREF_DEFAULTS[key]);
    return sanitizePref(key, JSON.parse(raw));
  } catch (_) {
    return sanitizePref(key, PREF_DEFAULTS[key]);
  }
}

export function hasPref(key) {
  if (!(key in PREF_DEFAULTS)) return false;
  try {
    return localStorage.getItem(PREFIX + key) !== null;
  } catch (_) {
    return false;
  }
}

export function setPref(key, value) {
  if (!(key in PREF_DEFAULTS)) return;
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(sanitizePref(key, value)));
  } catch (_) { /* quota or privacy mode — silent */ }
}

export function getAllPrefs() {
  const out = {};
  for (const k of Object.keys(PREF_DEFAULTS)) out[k] = getPref(k);
  return out;
}
